import { motion } from 'framer-motion';

import { createForm, useCreateFormStore } from '@components/form';
import { useScopedTranslate } from '@locale/translate';
import { isValidUrl } from '@utils/validators';

import { useUpdateCompanyInfo } from 'api/companies/hooks';
import { Company } from 'types/companies';

type CompanyInfoProps = Pick<Company, 'companyName' | 'url'>;

const Form = createForm<CompanyInfoProps>();

export const CompanyInfo = ({ companyName, url }: CompanyInfoProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const submit = useUpdateCompanyInfo({
    successMessage: t('successMessage'),
    errorMessage: t('errorMessage'),
  });

  const handleSave = async (data: CompanyInfoProps) => {
    if (!data.url) {
      return submit.mutate(data);
    }
    const websiteUrl = data.url.match(/^https?:\/\//) ? data.url : `https://${data.url}`;
    if (!isValidUrl(websiteUrl)) {
      return formStore.actions.setErrors({ url: 'valid-http-url' });
    }
    return submit.mutate({ ...data, url: websiteUrl });
  };

  return (
    <Form store={formStore} onSubmit={(data) => handleSave(data)}>
      <Form.TextField
        id="companyName"
        label={t('companyName')}
        validators={['required']}
        defaultValue={companyName}
      />
      <Form.TextField id="url" key={url} label={t('companyWebsite')} defaultValue={url ?? ''} />

      {isDirty && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Form.Actions>
            <Form.Reset text={t('cancel')} />
            <Form.Submit text={t('save')} variant="primary-blue" />
          </Form.Actions>
        </motion.div>
      )}
    </Form>
  );
};

export const locale = {
  en: {
    companyName: 'Company name',
    companyWebsite: 'Company website URL',
    cancel: 'Cancel',
    save: 'Save',
    successMessage: 'Company details successfully updated',
    errorMessage: 'Error while updating company details',
  },
  is: {
    companyName: 'Nafn fyrirtækis',
    companyWebsite: 'Fyrirtækjaslóð',
    cancel: 'Hætta við',
    save: 'Staðfesta',
    successMessage: 'Fyrirtækjaupplýsingum hefur verið breytt',
    errorMessage: 'Villa við að breyta fyrirtækjaupplýsingum',
  },
};
